import { AgGridColumnProps } from "ag-grid-react";
import { asCurrency, asDate } from "lqx-react/src";
import { getAssetErrors, getAssetStatus, getCellColor } from "../components/assets-table";
import { asTimelessDateUTC } from "lqx-react/lib";

export enum FilterName {
    Buyer = 'buyers',
    BuyerReferenceID = 'buyerReferenceIDs',
    Supplier = 'suppliers',
    SupplierReferenceID = 'supplierReferenceIDs',
    Reason = 'reasons',
    Amount = 'amounts',
    OutstandingAmount = 'outstandingAmounts',
    Currency = 'currencies',
    IssuanceDate = 'issuanceDates',
    Number = 'numbers',
    PaymentTerms = 'paymentTerms',
    ExpirationDate = 'expirationDates',
    SalesOrderNumber = 'salesOrderNumbers',
    PurchaseOrderNumber = 'purchaseOrderNumbers',
    InvoiceNumber = 'invoiceNumbers'
}

export enum FilterTypeCreditNotes {
    All = 0,
    ValidationCategory,
    AssetState,
    Buyer,
    BuyerReferenceID,
    Supplier,
    SupplierReferenceID,
    Reason,
    Amount,
    OutstandingAmount,
    Currency,
    IssuanceDate,
    Number,
    PaymentTerms,
    ExpirationDate,
    SalesOrderNumber,
    PurchaseOrderNumber,
    InvoiceNumber,
}

export enum CreditNotesSortingBase {
    Id = 0,
    CreatedOn,
    UpdatedOn,
    ValidationCategory,
    AssetState,
    Buyer,
    BuyerReferenceID,
    Supplier,
    SupplierReferenceID,
    Reason,
    Amount,
    OutstandingAmount,
    Currency,
    IssuanceDate,
    Number,
    PaymentTerms,
    ExpirationDate,
    SalesOrderNumber,
    PurchaseOrderNumber,
    InvoiceNumber
}

export enum CreditNotesTableColumns {
    Status = 0,
    Supplier,
    SupplierReferenceID,
    Buyer,
    BuyerReferenceID,
    Reason,
    Amount,
    OutstandingAmount,
    Currency,
    IssuanceDate,
    Number,
    PaymentTerms,
    ExpirationDate,
    SalesOrderNumber,
    PurchaseOrderNumber,
    InvoiceNumber,
    Errors
}

export const CreditNoteTableColumns: AgGridColumnProps[] = [
    {
        colId: 'CheckBox',
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true
    },
    {
        colId: CreditNotesTableColumns.Status.toString(),
        headerName: "Status",
        filter: 'agSetColumnFilter',
        filterParams: {
            values: ['Pass Validation - New', 'Pass Validation - Modified', 'Require Review', 'Duplicated', 'Conflict', 'Saved for Later', 'Digitized']
        },
        valueGetter: params => getAssetStatus(params.data),
        cellStyle: params => { return { color: getCellColor(params.data) } },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.Errors.toString(),
        headerName: "Errors",
        valueGetter: params => getAssetErrors(params.data)
    },
    {
        colId: CreditNotesTableColumns.Supplier.toString(),
        headerName: "Supplier",
        field: "changes.supplier.name", filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.Buyer.toString(),
        headerName: "Buyer",
        field: "changes.buyer.name", filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.Reason.toString(),
        headerName: "Reason",
        field: "changes.reason",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.Amount.toString(),
        headerName: "Amount",
        field: 'changes.amount',
        cellStyle: { textAlign: "right" },
        valueFormatter: asCurrency, filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.OutstandingAmount.toString(),
        headerName: "Outstanding Amount",
        field: 'changes.outstandingAmount',
        cellStyle: { textAlign: "right" },
        valueFormatter: asCurrency, filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.Currency.toString(),
        headerName: "Currency",
        field: "changes.currency", filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.IssuanceDate.toString(),
        headerName: "Issuance Date",
        field: 'changes.issuanceDate',
        valueFormatter: asTimelessDateUTC,
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.Number.toString(),
        headerName: "Credit Note#",
        field: "changes.number", filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.PaymentTerms.toString(),
        headerName: "Payment Terms",
        field: "changes.paymentTerms",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.ExpirationDate.toString(),
        headerName: "Expiration Date",
        field: 'changes.expirationDate',
        valueFormatter: asTimelessDateUTC, filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.SalesOrderNumber.toString(),
        headerName: "Sales Order Number",
        field: "changes.detail.saleOrder.number", filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.PurchaseOrderNumber.toString(),
        headerName: "Purchase Order Number",
        field: "changes.detail.purchaseOrder.number", filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: CreditNotesTableColumns.InvoiceNumber.toString(),
        headerName: "Invoice Number",
        field: "changes.detail.invoice.number",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    }
]
